<script setup>
import { computed, inject } from 'vue'

import ButtonComponent from '@/Components/ButtonComponent.vue'
import SvgVue from '@/Components/SvgVue.vue'

import useCart from '../useCart.js'

const emit = defineEmits(['removeDiscount'])

const cart = useCart(inject('cart'))
const cartModel = cart.cart
const checkoutOptions = inject('checkoutOptions')

const activeDiscount = computed(() => {
  return cartModel.value.discount || null
})
</script>

<template>
  <div class="my-3">
    <template v-if="cart.cartItem.value.quantity > 1">
      <div class="flex items-center py-1">
        <span>
          {{
            /* translators: Represents unit price label. Shown as part of the checkout summary table. */
            $t('lemonsqueezy_checkout.unit_price')
          }}
        </span>
        <span class="ml-auto" dusk="checkout-form-unit-price">
          <template v-if="cart.cartItem.value.price_model.is_tiered">
            {{
              /* translators: Represents product price, as in `Price starts from $59` */
              $t('lemonsqueezy_checkout.product_price.from', {
                price: formatCurrency(cart.cartItem.value.unit_price, cartModel.currency),
              })
            }}
          </template>
        </span>
      </div>
      <div class="flex items-center py-1">
        <span>{{
          /* translators: Represents cart item quantity label. Shown as part of the checkout summary table. */
          $t('lemonsqueezy_checkout.quantity')
        }}</span>
        <span class="ml-auto" dusk="checkout-form-quantity">
          {{ formatNumber(cart.cartItem.value.quantity) }}
        </span>
      </div>
      <div v-if="cart.cartItem.value.fixed_fee" class="flex items-center py-1">
        <span>{{
          /* translators: Represents fixed fee amount label. Shown as part of the checkout summary table. */
          $t('lemonsqueezy_checkout.fixed_fee')
        }}</span>
        <span class="ml-auto" dusk="checkout-form-fixed-fee">
          {{ formatCurrency(cart.cartItem.value.fixed_fee, cartModel.currency) }}
        </span>
      </div>
    </template>
    <div v-if="cart.hasSetupFee()" class="flex items-center py-1">
      <span>
        {{
          /* translators: Represents setup fee value label. Shown in the checkout summary table. */
          $t('lemonsqueezy_checkout.setup_fee')
        }}
      </span>
      <span class="ml-auto" dusk="checkout-form-setup-price">
        {{ formatCurrency(cartModel.setup_fee, cartModel.currency) }}
      </span>
    </div>
    <div class="flex items-center py-1">
      <span>{{
        /* translators: Represents checkout subtotal amount label. Shown as part of the checkout summary table. */
        $t('lemonsqueezy_checkout.subtotal')
      }}</span>
      <span class="ml-auto" dusk="checkout-form-subtotal-price">
        {{ formatCurrency(cartModel.subtotal, cartModel.currency) }}
      </span>
    </div>
    <div v-if="activeDiscount" class="flex items-center py-0.5">
      <span class="flex items-center overflow-hidden">
        {{ $t('lemonsqueezy_checkout.discount') }}
        <span
          class="ml-1 inline-block rounded bg-wedges-gray-50 px-[10px] py-[2px] font-mono text-12 uppercase tracking-widest text-wedges-gray-900"
        >
          <span class="block max-w-30 truncate">
            {{ activeDiscount.code }}
          </span>
        </span>
        <ButtonComponent
          v-if="checkoutOptions.discount"
          variant="plain"
          dusk="checkout-form-remove-discount"
          @click="emit('removeDiscount')"
        >
          <SvgVue icon="Cross Small" class="h-3 w-3 stroke-current text-wedges-gray-400" />
        </ButtonComponent>
      </span>
      <span class="ml-auto"> -{{ formatCurrency(cartModel.discount_total, cartModel.currency) }} </span>
    </div>
    <div v-if="activeDiscount && $parent.form.errors.discount_code" class="flex items-center py-0.5">
      <p class="text-wtf-paradise">
        {{ $parent.form.errors.discount_code }}
      </p>
    </div>
    <div v-if="cartModel.tax_name && cartModel.tax > 0" class="flex items-center py-1" dusk="checkout-form-tax-row">
      <span>
        {{ cartModel.tax_name }}
        ({{ cartModel.tax_rate }}%{{ cartModel.tax_inclusive ? ' included in total' : '' }})
      </span>
      <span class="ml-auto" dusk="checkout-form-tax-price">
        {{ formatCurrency(cartModel.tax, cartModel.currency) }}
      </span>
    </div>
    <div class="flex items-center py-1">
      <span v-if="cart.priceModel.value.renewal_interval_unit === 'day'">
        {{
          /* translators: The renewal interval of the subscription (in days). For example `Every 2 days` */
          $t('lemonsqueezy_checkout.renewal_interval.day', {
            intervalQuantity: cart.priceModel.value.renewal_interval_quantity,
          })
        }}
      </span>

      <span v-if="cart.priceModel.value.renewal_interval_unit === 'week'">
        {{
          /* translators: The renewal interval of the subscription (in weeks). For example `Every 2 weeks` */
          $t('lemonsqueezy_checkout.renewal_interval.week', {
            intervalQuantity: cart.priceModel.value.renewal_interval_quantity,
          })
        }}
      </span>

      <span v-if="cart.priceModel.value.renewal_interval_unit === 'month'">
        {{
          /* translators: The renewal interval of the subscription (in years). For example `Every 2 months` */
          $t('lemonsqueezy_checkout.renewal_interval.month', {
            intervalQuantity: cart.priceModel.value.renewal_interval_quantity,
          })
        }}
      </span>

      <span v-if="cart.priceModel.value.renewal_interval_unit === 'year'">
        {{
          /* translators: The renewal interval of the subscription (in years). For example `Every 2 years` */
          $t('lemonsqueezy_checkout.renewal_interval.year', {
            intervalQuantity: cart.priceModel.value.renewal_interval_quantity,
          })
        }}
      </span>
      <span class="ml-auto">
        {{ formatCurrency(cartModel.recurring_total, cartModel.currency) }}
      </span>
    </div>
    <div v-if="!cart.hasSetupFee()" class="flex items-center py-0.75 text-16 font-medium">
      <span v-if="cart.priceModel.value.trial_interval_unit === 'day'">
        {{
          /* translators: The trial period of the product in weeks. For example `1 day trial`. */
          $t('lemonsqueezy_checkout.trial_interval.day', {
            trialIntervalQuantity: cart.priceModel.value.trial_interval_quantity,
          })
        }}
      </span>

      <span v-if="cart.priceModel.value.trial_interval_unit === 'week'">
        {{
          /* translators: The trial period of the product in weeks. For example `1 week trial`. */
          $t('lemonsqueezy_checkout.trial_interval.week', {
            trialIntervalQuantity: cart.priceModel.value.trial_interval_quantity,
          })
        }}
      </span>

      <span v-if="cart.priceModel.value.trial_interval_unit === 'month'">
        {{
          /* translators: The trial period of the product in months. For example `1 month trial`. */
          $t('lemonsqueezy_checkout.trial_interval.month', {
            trialIntervalQuantity: cart.priceModel.value.trial_interval_quantity,
          })
        }}
      </span>

      <span v-if="cart.priceModel.value.trial_interval_unit === 'year'">
        {{
          /* translators: The trial period of the product in years. For example `1 year trial`. */
          $t('lemonsqueezy_checkout.trial_interval.year', {
            trialIntervalQuantity: cart.priceModel.value.trial_interval_quantity,
          })
        }}
      </span>
      <span class="ml-auto uppercase">{{
        /* translators: The price of the product. This says that the product is free. */
        $t('lemonsqueezy_checkout.product_price.free')
      }}</span>
    </div>
    <div v-else class="flex items-center py-0.75 text-16 font-medium">
      <span>
        {{
          /* translators: Represents checkout total amount label. Shown as part of the checkout summary table. */
          $t('lemonsqueezy_checkout.total')
        }}
      </span>
      <span class="ml-auto" dusk="checkout-form-total-price">
        {{ formatCurrency(cartModel.total, cartModel.currency) }}
      </span>
    </div>
  </div>
</template>
